'use client'
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro'
import { formatDate } from '@repo/utils/time'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { useBankFeedsStore } from '@repo/store/useBankFeedsStore'
import { useEffect, useState } from 'react'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'

const formatLastSync = (lastSyncDate) => (lastSyncDate ? formatDate(lastSyncDate) : 'Never synced')

const formatOldestUnmatchedAge = (oldestUnmatchedTransactionDate) => {
  if (!oldestUnmatchedTransactionDate) return '—'

  const oldestDate = new Date(oldestUnmatchedTransactionDate)
  if (Number.isNaN(oldestDate.getTime())) return '—'

  const diffDays = Math.floor((Date.now() - oldestDate.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays <= 0) return 'Today'

  return diffDays === 1 ? '1 day' : `${diffDays} days`
}

const columns = [
  { field: 'ospreyBankName', headerName: 'Bank name', flex: 1 },
  { field: 'institutionName', headerName: 'Institution', flex: 1 },
  {
    field: 'lastSyncDate',
    headerName: 'Last sync',
    flex: 1,
    renderCell: (params) => <span>{formatLastSync(params.value)}</span>
  },
  { field: 'unmatchedCount', headerName: 'Unmatched transactions', flex: 1, type: 'number' },
  {
    field: 'oldestUnmatchedTransactionDate',
    headerName: 'Oldest unmatched',
    flex: 1,
    renderCell: (params) => <span>{formatOldestUnmatchedAge(params.value)}</span>
  }
]

const BankReconciliationStatus = () => {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])

  const { fetchAllBankFeeds } = useBankFeedsStore()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { enableBankFeeds, enableAutomatedBankReconciliation } = useLookupsStore()

  useEffect(() => {
    if (!enableBankFeeds || !enableAutomatedBankReconciliation) return

    let isCancelled = false

    const fetchReconciliationStatus = async () => {
      setLoading(true)

      try {
        const linkedAccounts = await fetchAllBankFeeds()

        const accountsWithSummary = await Promise.all(
          (linkedAccounts ?? []).map(async (account) => {
            const summary = await ospreyAPI({
              service: 'banking',
              endpoint: '/PlaidBankAccount/GetReconciliationStatusSummary',
              method: 'GET',
              data: { plaidBankAccountId: account.plaidBankAccountId }
            })

            return { ...account, ...summary }
          })
        )

        if (!isCancelled) setRows(accountsWithSummary)
      } catch (error) {
        console.error('Error fetching reconciliation status summary:', error)
      } finally {
        if (!isCancelled) setLoading(false)
      }
    }

    fetchReconciliationStatus()

    return () => {
      isCancelled = true
    }
  }, [fetchAllBankFeeds, enableBankFeeds, enableAutomatedBankReconciliation])

  if (!enableBankFeeds || !enableAutomatedBankReconciliation) {
    return <MUIStack data-testid="BankReconciliationStatus"></MUIStack>
  }

  return (
    <MUIStack
      data-testid="BankReconciliationStatus"
      sx={{ mt: pageTitleBlockIsVisible ? 0 : '10rem', m: '0 4rem 4rem' }}
    >
      <MUIPaper sx={{ height: 'auto', width: '100%' }}>
        <DataGridPro
          columns={columns}
          disableVirtualization
          getRowId={(row) => row.plaidBankAccountId}
          loading={loading}
          rows={rows}
          slotProps={{
            loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' }
          }}
          slots={{
            headerFilterMenu: null
          }}
          sx={{
            [`.MuiDataGrid-root`]: { border: 'none', height: 'auto' },
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
            [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: { outline: 'none' }
          }}
        />
      </MUIPaper>
    </MUIStack>
  )
}

export default BankReconciliationStatus
